import KernelTimezoneSelect from '../components/KernelTimezoneSelect';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getUsers, getCompanies, createUser, updateUser, deleteUser, reactivateUser, getRoles, errorMessage } from '../services/api';
import { useLanguage, SUPPORTED_LOCALES } from '../i18n/LanguageContext';
import { adminConfig, ActionsWrap, makeRoleHelpers, KernelPage } from '../kernelAdminShared';
import { PHONE_COUNTRY_CODES, DEFAULT_PHONE_PREFIX, splitPhone, combinePhone } from '../utils/phoneCodes';
import { searchTerms, matchesAllTerms } from '../utils/kernelSearch';
import KernelSearchBox from '../components/KernelSearchBox';
import KernelHighlight from '../components/KernelHighlight';
import KernelPasswordField, { KernelPasswordRules } from '../components/KernelPasswordField';
import { isPasswordValid } from '../utils/kernelPassword';
import KernelLoginHistory from '../components/KernelLoginHistory';
import { fmtDateTime } from '../utils/kernelFormat';

const emptyForm = {
  name: '',
  surname: '',
  email: '',
  phone: '',
  phone_mobile: '',
  password: '',
  confirm: '',
  role: '2',
  lang: 'it-IT',
  timezone: '',
};

function CompanyUsers({ user, onLogout }) {
  const { t } = useLanguage();
  const { id } = useParams();
  const navigate = useNavigate();
  const companyId = Number(id);

  const [users, setUsers] = useState([]);
  const [companyName, setCompanyName] = useState('');
  const [companyDefaultLang, setCompanyDefaultLang] = useState('it-IT');
  const [activeUsers, setActiveUsers] = useState(0);
  const [maxUsers, setMaxUsers] = useState(0);
  const [statusFilter, setStatusFilter] = useState('active');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [officePrefix, setOfficePrefix] = useState(DEFAULT_PHONE_PREFIX);
  const [mobilePrefix, setMobilePrefix] = useState(DEFAULT_PHONE_PREFIX);
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState([]);
  const [warning, setWarning] = useState('');
  const { roleName, roleOptions } = makeRoleHelpers(t, roles);
  const [search, setSearch] = useState('');
  const [historyUser, setHistoryUser] = useState(null);

  const isSupportoAgency = companyId === 990001;

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const [usersData, companiesData, rolesData] = await Promise.all([
        getUsers(true),
        getCompanies(),
        adminConfig.dynamicRoles ? getRoles() : null,
      ]);
      if (rolesData) setRoles(rolesData.roles || []);
      setUsers((usersData.users || []).filter((u) => Number(u.company_id) === companyId));
      const company = (companiesData.companies || []).find((c) => Number(c.id) === companyId);
      setCompanyName(company ? company.name : '');
      setCompanyDefaultLang(company?.default_lang || 'it-IT');
      setActiveUsers(company?.active_users ?? 0);
      setMaxUsers(company?.max_users ?? 0);
    } catch (err) {
      setError(errorMessage(err, t('companyUsers.errorLoad')));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const resetMessages = () => {
    setError('');
    setSuccess('');
    setWarning('');
  };

  const startCreate = () => {
    resetMessages();
    setForm({ ...emptyForm, lang: companyDefaultLang });
    setOfficePrefix(DEFAULT_PHONE_PREFIX);
    setMobilePrefix(DEFAULT_PHONE_PREFIX);
    setEditId(null);
    setShowForm(true);
  };

  const startEdit = (u) => {
    resetMessages();
    const office = splitPhone(u.phone);
    const mobile = splitPhone(u.phone_mobile);
    setForm({
      name: u.name || '',
      surname: u.surname || '',
      email: u.email || '',
      phone: office.number,
      phone_mobile: mobile.number,
      password: '',
      confirm: '',
      role: String(u.role || '2'),
      lang: u.lang || 'it-IT',
      timezone: u.timezone || '',
    });
    setOfficePrefix(office.prefix);
    setMobilePrefix(mobile.prefix);
    setEditId(u.id);
    setShowForm(true);
  };

  const handleDeactivate = async (u) => {
    if (!window.confirm(t('companyUsers.deactivateConfirm', { name: `${u.name} ${u.surname || ''}`.trim() }))) {
      return;
    }
    resetMessages();
    try {
      const result = await deleteUser(u.id);
      setSuccess(t('companyUsers.deactivated'));
      const clearedProjects = result?.cleared_default_assignee_projects || [];
      if (adminConfig.deleteWarning && clearedProjects.length > 0) {
        setWarning(t('companyUsers.deactivatedClearedAssignee', { projects: clearedProjects.map((p) => p.name).join(', ') }));
      }
      fetchData();
    } catch (err) {
      setError(errorMessage(err, t('companyUsers.errorSave')));
    }
  };

  const handleReactivate = async (u) => {
    resetMessages();
    try {
      await reactivateUser(u.id);
      setSuccess(t('companyUsers.reactivated'));
      fetchData();
    } catch (err) {
      setError(errorMessage(err, t('companyUsers.errorSave')));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    resetMessages();

    if (!form.name.trim()) {
      setError(t('companyUsers.errorName'));
      return;
    }
    if (!form.email.trim()) {
      setError(t('companyUsers.errorEmail'));
      return;
    }
    if (!editId) {
      if (!isPasswordValid(form.password)) {
        setError(t('companyUsers.errorPassword'));
        return;
      }
      if (form.password !== form.confirm) {
        setError(t('companyUsers.errorMismatch'));
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        surname: form.surname.trim(),
        email: form.email.trim().toLowerCase(),
        phone: combinePhone(officePrefix, form.phone),
        phone_mobile: combinePhone(mobilePrefix, form.phone_mobile),
        company_id: companyId,
        role: Number(form.role),
        lang: form.lang,
        timezone: form.timezone,
      };
      if (editId) {
        await updateUser(editId, payload);
        setSuccess(t('companyUsers.updated'));
      } else {
        payload.password = form.password;
        await createUser(payload);
        setSuccess(t('companyUsers.created'));
      }
      setShowForm(false);
      fetchData();
    } catch (err) {
      setError(errorMessage(err, t('companyUsers.errorSave')));
    } finally {
      setSaving(false);
    }
  };

  // Ricerca: più parole separate da spazio, devono comparire tutte (AND), nel testo che si vede in tabella.
  const terms = searchTerms(search);
  const statusLabel = (u) => (Number(u.status) === 1 ? t('companyUsers.active') : t('companyUsers.inactive'));
  const visibleUsers = users.filter((u) => {
    if (statusFilter === 'active' && Number(u.status) !== 1) return false;
    if (statusFilter === 'inactive' && Number(u.status) !== 4) return false;
    return matchesAllTerms(
      [`${u.name} ${u.surname || ''}`, u.email, u.phone || '', u.phone_mobile || '', roleName(u.role), statusLabel(u)].join(' '),
      terms
    );
  });

  const atLimit = maxUsers > 0 && activeUsers >= maxUsers;
  const requiredEmpty = (v) => (v === '' || v == null ? ' required-empty' : '');

  if (historyUser) {
    return (
      <KernelPage user={user} onLogout={onLogout}>
        <KernelLoginHistory person={historyUser} currentUserId={user?.id} onClose={() => setHistoryUser(null)} />
      </KernelPage>
    );
  }

  return (
    <KernelPage user={user} onLogout={onLogout}>
      <div className="page-header">
        <div>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>
            {t('companyUsers.back')}
          </button>
          <h1 className="page-title" style={{ marginTop: 10 }}>
            {t('companyUsers.title', { name: companyName })}
          </h1>
        </div>
        {!showForm && (
          <button
            className="btn"
            type="button"
            onClick={startCreate}
            disabled={atLimit}
            title={atLimit ? t('companyUsers.limitReached', { active: activeUsers, max: maxUsers }) : undefined}
          >
            {t('companyUsers.newUser')}
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      {warning && <div className="warning-message">{warning}</div>}

      {!showForm && (
        <div className="stats-grid" style={{ marginBottom: 16 }}>
          <div className="stat-card">
            <div className="stat-label">{t('companyUsers.activeUsersLabel')}</div>
            <div className="stat-value">{activeUsers} / {maxUsers || '∞'}</div>
          </div>
        </div>
      )}

      {!showForm && atLimit && (
        <div className="warning-message" style={{ marginBottom: 16 }}>
          {t('companyUsers.limitReached', { active: activeUsers, max: maxUsers })}
        </div>
      )}

      {!showForm && (
        <div className="form-group" style={{ maxWidth: 220, marginBottom: 16 }}>
          <label>{t('companyUsers.statusFilter')}</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="active">{t('companyUsers.filterActive')}</option>
            <option value="inactive">{t('companyUsers.filterInactive')}</option>
            <option value="all">{t('companyUsers.filterAll')}</option>
          </select>
        </div>
      )}

      {loading && <div className="loading">{t('common.loading')}</div>}

      {!loading && !showForm && (
        <>
        <div className="ksb-bar">
          <KernelSearchBox value={search} onChange={setSearch} placeholder={t('companyUsers.search')} title={t('companyUsers.searchHint')} />
        </div>
        <div className="card">
          <div className="card-body table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('companyUsers.name')}</th>
                  <th>{t('companyUsers.email')}</th>
                  <th>{t('companyUsers.phone')}</th>
                  <th>{t('companyUsers.phoneMobile')}</th>
                  <th>{t('companyUsers.role')}</th>
                  <th>{t('companyUsers.status')}</th>
                  <th>{t('loginHistory.lastLogin')}</th>
                  <th className="actions">{t('companyUsers.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {visibleUsers.map((u) => (
                  <tr key={u.id} className={Number(u.status) === 4 ? 'row-inactive' : ''}>
                    <td><KernelHighlight text={`${u.name} ${u.surname || ''}`.trim()} terms={terms} /></td>
                    <td><KernelHighlight text={u.email} terms={terms} /></td>
                    <td>{u.phone ? <KernelHighlight text={u.phone} terms={terms} /> : '—'}</td>
                    <td>{u.phone_mobile ? <KernelHighlight text={u.phone_mobile} terms={terms} /> : '—'}</td>
                    <td><KernelHighlight text={roleName(u.role)} terms={terms} /></td>
                    <td>
                      <span className={`badge ${Number(u.status) === 1 ? 'badge-success' : 'badge-warning'}`}>
                        <KernelHighlight text={statusLabel(u)} terms={terms} />
                      </span>
                    </td>
                    <td className="nowrap">{u.last_login_at ? fmtDateTime(u.last_login_at) : <span style={{ opacity: 0.6 }}>{t('loginHistory.never')}</span>}</td>
                    <td className="actions">
                      <ActionsWrap>
                        <button
                          type="button"
                          className="icon-btn"
                          title={t('loginHistory.show')}
                          onClick={() => setHistoryUser(u)}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" />
                            <polyline points="12 6 12 12 16 14" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          className="icon-btn"
                          title={t('adminUsers.edit')}
                          onClick={() => startEdit(u)}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                          </svg>
                        </button>
                        {Number(u.status) === 1 ? (
                          <button
                            type="button"
                            className="icon-btn icon-btn-danger"
                            title={t('companyUsers.deactivate')}
                            onClick={() => handleDeactivate(u)}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10" />
                              <line x1="8" y1="12" x2="16" y2="12" />
                            </svg>
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="icon-btn"
                            title={t('companyUsers.reactivate')}
                            onClick={() => handleReactivate(u)}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10" />
                              <polyline points="9 12 11 14 15 10" />
                            </svg>
                          </button>
                        )}
                      </ActionsWrap>
                    </td>
                  </tr>
                ))}
                {visibleUsers.length === 0 && (
                  <tr>
                    <td colSpan="8" className="empty-cell">
                      {terms.length > 0 ? t('companyUsers.noResults') : t('companyUsers.empty')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        </>
      )}

      {!loading && showForm && (
        <div className="card">
          <div className="card-body">
            <h2 className="form-section-title">
              {editId ? t('adminUsers.editTitle') : t('adminUsers.newTitle')}
            </h2>
            <form onSubmit={handleSubmit} className="admin-form">
              <div className="form-row-2">
                <div className={`form-group${requiredEmpty(form.name)}`}>
                  <label>{t('companyUsers.name')} *</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>{t('adminUsers.surname')}</label>
                  <input
                    type="text"
                    value={form.surname}
                    onChange={(e) => setForm({ ...form, surname: e.target.value })}
                  />
                </div>
              </div>

              <div className={`form-group${requiredEmpty(form.email)}`}>
                <label>{t('companyUsers.email')} *</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                />
              </div>

              <div className="form-row-2">
                <div className="form-group">
                  <label>{t('companyUsers.phone')}</label>
                  <div className="phone-input-group">
                    <select value={officePrefix} onChange={(e) => setOfficePrefix(e.target.value)}>
                      {PHONE_COUNTRY_CODES.map((c) => (
                        <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>{t('companyUsers.phoneMobile')}</label>
                  <div className="phone-input-group">
                    <select value={mobilePrefix} onChange={(e) => setMobilePrefix(e.target.value)}>
                      {PHONE_COUNTRY_CODES.map((c) => (
                        <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      value={form.phone_mobile}
                      onChange={(e) => setForm({ ...form, phone_mobile: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              <div className="form-row-2">
                <div className="form-group">
                  <label>{t('adminUsers.lang')}</label>
                  <select value={form.lang} onChange={(e) => setForm({ ...form, lang: e.target.value })}>
                    {SUPPORTED_LOCALES.map((code) => (
                      <option key={code} value={code}>{t(`languages.${code}`)}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>{t('timezone.label')}</label>
                  <KernelTimezoneSelect
                    value={form.timezone}
                    onChange={(zone) => setForm({ ...form, timezone: zone })}
                    companyZone={user?.company_timezone}
                  />
                </div>
                <div className="form-group">
                  <label>{t('companyUsers.role')} *</label>
                  <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                    {roleOptions(isSupportoAgency).map((r) => (
                      <option key={r.id} value={String(r.id)}>{r.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {!editId && (
                <div className="form-row-2">
                  <KernelPasswordField
                    label={`${t('adminUsers.password')} *`}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    autoComplete="new-password"
                  />
                  <KernelPasswordField
                    label={`${t('adminUsers.confirm')} *`}
                    value={form.confirm}
                    onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                    autoComplete="new-password"
                  />
                </div>
              )}
              {!editId && <KernelPasswordRules value={form.password} />}

              <div className="form-actions">
                <button type="submit" className="btn" disabled={saving}>
                  {saving ? t('common.saving') : t('common.save')}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>
                  {t('common.cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </KernelPage>
  );
}

export default CompanyUsers;
